import React, { useCallback, useState } from 'react';
import { TabsContent, ConfirmationDialog } from '../../../components/ui';
import { DocumentTextIcon, FileIcon } from '../../../components/ui/Icons';
import { CustomSplitViewWithTabs } from '../../../components/generic/SplitViewWithTabs';
import { useFlexibleList } from '../../../hooks/useSWR';
import { mutate } from 'swr';
import { API_BASE_URL } from '../../../config/apiConfig';
import { MANUAL_ENTITY_TYPE_ID } from '../manualConfig';
import { mapManualAssignmentFromApi } from './mapManualAssignment';
import MyManualsDetailsTab from './components/MyManualsDetailsTab';
import MyManualsFilesTab from './components/MyManualsFilesTab';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const savedUser = localStorage.getItem('user');
  let userId = null;
  if (savedUser) {
    try {
      userId = JSON.parse(savedUser).id?.toString();
    } catch {
      userId = null;
    }
  }
  return {
    'Content-Type': 'application/json',
    ...(userId && { 'user-id': userId }),
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const CustomMyManualsSplitView = ({
  isOpen,
  selectedRecord,
  config,
  fields,
  labels,
  onClose,
  onRecordUpdate,
}) => {
  const manualId =
    selectedRecord?.canView !== false ? selectedRecord?.manualId : null;

  const { data: manualFiles = [] } = useFlexibleList(
    manualId ? `/file-attachment/entity/${MANUAL_ENTITY_TYPE_ID}/${manualId}` : null
  );

  const activeFiles = (manualFiles || []).filter((f) => !f.isDeleted);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [ackError, setAckError] = useState(null);
  const [ackConfirmOpen, setAckConfirmOpen] = useState(false);

  const isPending =
    selectedRecord?.canView !== false && selectedRecord?.viewStatus !== 'VIEWED';

  const acknowledgeManual = useCallback(async () => {
    if (!selectedRecord?.assignmentId || selectedRecord.canView === false) return;
    if (selectedRecord.viewStatus === 'VIEWED') return;

    setIsAcknowledging(true);
    setAckError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/manual-assignment/me/${selectedRecord.assignmentId}/view`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to acknowledge manual');
      }
      const dto = await res.json();
      const mapped = mapManualAssignmentFromApi(dto);
      await mutate('/manual-assignment/me');
      setAckConfirmOpen(false);
      onRecordUpdate?.(mapped);
    } catch (e) {
      setAckError(e.message || 'Failed to acknowledge');
    } finally {
      setIsAcknowledging(false);
    }
  }, [selectedRecord, onRecordUpdate]);

  const tabs = [
    {
      value: 'details',
      label: 'Details',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge: null,
    },
    {
      value: 'files',
      label: 'Files',
      icon: <FileIcon className="w-4 h-4" />,
      badge: selectedRecord?.canView !== false ? activeFiles.length : null,
      badgeColor: 'bg-blue-600',
    },
  ];

  return (
    <>
      <CustomSplitViewWithTabs
        isOpen={isOpen}
        config={config}
        labels={labels}
        headerTitle={
          selectedRecord
            ? `${selectedRecord.code || ''} — ${selectedRecord.title || selectedRecord.id}`
            : labels.noSelectionText
        }
        selectedRecord={selectedRecord}
        emptyMessage={labels.noSelectionText}
        tabs={tabs}
        onClose={onClose}
        defaultTab="details"
      >
        <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
          <MyManualsDetailsTab
            selectedRecord={selectedRecord}
            isPending={isPending}
            isAcknowledging={isAcknowledging}
            ackError={ackError}
            onAcknowledge={() => setAckConfirmOpen(true)}
          />
        </TabsContent>
        <TabsContent value="files" className="mt-0 flex-1 flex flex-col min-h-0">
          <MyManualsFilesTab
            files={activeFiles}
            canView={selectedRecord?.canView !== false}
          />
        </TabsContent>
      </CustomSplitViewWithTabs>

      <ConfirmationDialog
        isOpen={ackConfirmOpen}
        onClose={() => {
          if (!isAcknowledging) setAckConfirmOpen(false);
        }}
        onConfirm={acknowledgeManual}
        title="Acknowledge manual"
        message="Confirm that you have reviewed this manual and its attachments?"
        details={
          selectedRecord
            ? {
                Code: selectedRecord.code || '—',
                Title: selectedRecord.title || '—',
              }
            : null
        }
        warningMessage="This action is recorded and cannot be undone by you."
        confirmText="Acknowledge"
        cancelText="Cancel"
        confirmVariant="default"
        isLoading={isAcknowledging}
      />
    </>
  );
};

export default CustomMyManualsSplitView;
